import type { DentalAsset } from './types';

const AUTH_KEY = 'dental-cloud-auth';
interface Auth { token: string; user: { id: string; login: string } }
export function currentAuth(): Auth | null {
  try { return JSON.parse(sessionStorage.getItem(AUTH_KEY) || 'null'); }
  catch { return null; }
}
export function logout() { sessionStorage.removeItem(AUTH_KEY); window.location.assign('/app/login'); }
export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}
async function responseFor(path: string, options: RequestInit = {}) {
  const headers = new Headers(options.headers);
  const auth = currentAuth();
  if (auth) headers.set('Girder-Token', auth.token);
  const response = await fetch(`/api/v1/${path}`, { ...options, headers, credentials: 'same-origin' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(response.status, body.message || `Request failed (${response.status}).`);
  }
  return response;
}
export async function api<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
  return (await responseFor(path, { method, ...(body === undefined ? {} : {
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  }) })).json();
}
export async function authenticate(login: string, password: string) {
  const bytes = new TextEncoder().encode(`${login}:${password}`);
  const result = await (await responseFor('user/authentication', {
    headers: { Authorization: `Basic ${btoa(String.fromCharCode(...bytes))}` },
  })).json();
  sessionStorage.setItem(AUTH_KEY, JSON.stringify({ token: result.authToken.token,
    user: { id: result.user._id, login: result.user.login } }));
}
export async function assetFile(asset: DentalAsset): Promise<File> {
  const response = await responseFor(`file/${asset.fileId}/download`);
  return new File([await response.blob()], asset.name, { type: 'application/octet-stream' });
}
export async function assetFiles(asset: DentalAsset): Promise<File[]> {
  if (asset.format !== 'dicom-series') return [await assetFile(asset)];
  if (!asset.dicomInstances?.length) throw new Error('The DICOM series has no registered instances.');
  const files: File[] = [];
  // Bound simultaneous transfers/memory; the loader categorizes the full series.
  for (const row of asset.dicomInstances) {
    const response = await responseFor(`file/${row.fileId}/download`);
    files.push(new File([await response.blob()], row.name, { type: 'application/dicom' }));
  }
  return files;
}
export async function uploadFile(folderId: string, file: File): Promise<string> {
  const query = new URLSearchParams({ parentType: 'folder', parentId: folderId, name: file.name,
    size: String(file.size), mimeType: file.type || 'application/octet-stream' });
  let upload = await (await responseFor(`file?${query}`, { method: 'POST' })).json();
  if (upload.itemId) return upload._id;
  let offset = Number(upload.received || 0);
  while (offset < file.size) {
    const block = file.slice(offset, Math.min(file.size, offset + 8 * 1024 * 1024));
    const params = new URLSearchParams({ uploadId: upload._id, offset: String(offset) });
    const next = await (await responseFor(`file/chunk?${params}`, {
      method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: block,
    })).json();
    offset += block.size;
    if (next.itemId) return next._id;
    if (Number(next.received) !== offset) throw new Error('Upload offset was not confirmed. Retry before saving.');
    upload = next;
  }
  throw new Error('Upload was not finalized; no annotation revision was saved.');
}
export function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob); const link = document.createElement('a');
  link.href = url; link.download = name; document.body.appendChild(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
